import { create } from "@bufbuild/protobuf";
import { useQuery } from "@tanstack/react-query";
import { journalServiceClient } from "@/connect";
import type { HolidayInfo } from "@/types/proto/api/v1/memo_service_pb";
import { ListActivityStatsRequestSchema } from "@/types/proto/api/v1/memo_service_pb";

// Query keys factory
export const activityStatsKeys = {
  all: ["activityStats"] as const,
  lists: () => [...activityStatsKeys.all, "list"] as const,
  list: (year: number) => [...activityStatsKeys.lists(), year] as const,
};

export interface UseActivityStatsOptions {
  parent?: string;
  startDate: string;
  endDate: string;
  enabled?: boolean;
}

export interface ActivityStatsData {
  activityStats: Record<string, number>;
  holidays: Record<string, HolidayInfo>;
}

/**
 * Hook for fetching activity stats for a given date range.
 * Used for year-based loading to support both month and year calendar views.
 */
export function useActivityStats(options: UseActivityStatsOptions) {
  const { parent, startDate, endDate, enabled = true } = options;

  const startDateInt = BigInt(parseInt(startDate.replace(/-/g, ""), 10));
  const endDateInt = BigInt(parseInt(endDate.replace(/-/g, ""), 10));
  const parentResource = parent || "users/me";

  // v0.65.0: Backend auto-detects timezone from user_setting
  // Timezone parameter is deprecated - removed from request (Invariant I2)
  // TimezoneContext saves timezone to user_setting on app load

  // Extract year from startDate for cache key
  const year = parseInt(startDate.substring(0, 4));

  return useQuery({
    queryKey: activityStatsKeys.list(year),
    queryFn: async () => {
      const response = await journalServiceClient.listActivityStats(
        create(ListActivityStatsRequestSchema, {
          parent: parentResource,
          startDate: startDateInt,
          endDate: endDateInt,
          // timezone field removed (deprecated v0.65.0)
          // Backend loads timezone from user_setting table automatically
        }),
      );

      return {
        activityStats: response.activityStats,
        holidays: response.holidays,
      } as ActivityStatsData;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });
}
